import React from 'react';

interface MicronutrientMatrixProps {
  micronutrientData: any;
  isLoading?: boolean;
  clientMode?: boolean;
  clientName?: string;
  userRecommendations?: any; // Personalized recommendations from user profile
}

// RDA values for adults (approximate)
const RDA_VALUES = {
  // Macronutrients
  calories: { value: 2500, unit: 'kcal' },
  protein: { value: 56, unit: 'g' },
  carbs: { value: 300, unit: 'g' },
  fat: { value: 78, unit: 'g' },
  fiber: { value: 25, unit: 'g' },
  
  // Vitamins
  vitamin_a: { value: 900, unit: 'mcg' },
  vitamin_c: { value: 90, unit: 'mg' },
  vitamin_d: { value: 15, unit: 'mcg' },
  vitamin_e: { value: 15, unit: 'mg' },
  vitamin_k: { value: 120, unit: 'mcg' },
  thiamine_b1: { value: 1.2, unit: 'mg' },
  riboflavin_b2: { value: 1.3, unit: 'mg' },
  niacin_b3: { value: 16, unit: 'mg' },
  pantothenic_acid_b5: { value: 5, unit: 'mg' },
  pyridoxine_b6: { value: 1.3, unit: 'mg' },
  biotin_b7: { value: 30, unit: 'mcg' },
  folate_b9: { value: 400, unit: 'mcg' },
  vitamin_b12: { value: 2.4, unit: 'mcg' },
  
  // Minerals
  calcium: { value: 1000, unit: 'mg' },
  iron: { value: 8, unit: 'mg' },
  magnesium: { value: 400, unit: 'mg' },
  zinc: { value: 11, unit: 'mg' },
  potassium: { value: 3500, unit: 'mg' },
  sodium: { value: 2300, unit: 'mg' },
  phosphorus: { value: 700, unit: 'mg' },
  copper: { value: 0.9, unit: 'mg' },
  selenium: { value: 55, unit: 'mcg' },
  chromium: { value: 35, unit: 'mcg' },
  molybdenum: { value: 45, unit: 'mcg' },
  iodine: { value: 150, unit: 'mcg' },
  
  // Amino Acids (estimated daily needs in mg)
  histidine: { value: 700, unit: 'mg' },
  isoleucine: { value: 1400, unit: 'mg' },
  leucine: { value: 2730, unit: 'mg' },
  lysine: { value: 2100, unit: 'mg' },
  methionine: { value: 1050, unit: 'mg' },
  phenylalanine: { value: 1750, unit: 'mg' },
  threonine: { value: 1050, unit: 'mg' },
  tryptophan: { value: 280, unit: 'mg' },
  valine: { value: 1820, unit: 'mg' },
  
  // Fatty Acids (approximate targets)
  saturated: { value: 20, unit: 'g' },
  monounsaturated: { value: 25, unit: 'g' },
  polyunsaturated: { value: 17, unit: 'g' },
  omega_3: { value: 1.6, unit: 'g' },
  omega_6: { value: 17, unit: 'g' },
};

const getColorClass = (value: number, rda: number) => {
  const percentage = (value / rda) * 100;
  
  if (percentage >= 100) return 'bg-green-50 border-green-200 text-green-800';
  if (percentage >= 75) return 'bg-yellow-50 border-yellow-200 text-yellow-800';
  if (percentage >= 50) return 'bg-orange-50 border-orange-200 text-orange-800';
  return 'bg-red-50 border-red-200 text-red-800';
};

const getStatusIcon = (value: number, rda: number) => {
  const percentage = (value / rda) * 100;
  
  if (percentage >= 100) return '✅';
  if (percentage >= 75) return '⚠️';
  if (percentage >= 50) return '🟡';
  return '🔴';
};

const formatValue = (value: number) => {
  if (value < 1) return value.toFixed(2);
  if (value < 10) return value.toFixed(1);
  return Math.round(value).toString();
};

const formatNutrientName = (key: string) => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
    .replace(' B1', ' (B1)')
    .replace(' B2', ' (B2)')
    .replace(' B3', ' (B3)')
    .replace(' B5', ' (B5)')
    .replace(' B6', ' (B6)')
    .replace(' B7', ' (B7)')
    .replace(' B9', ' (B9)')
    .replace(' B12', ' (B12)');
};

export const MicronutrientMatrix: React.FC<MicronutrientMatrixProps> = ({
  micronutrientData,
  userRecommendations
}) => {
  if (!micronutrientData?.daily_averages) {
    return (
      <div className="card">
        <div className="card-body">
          <p className="text-gray-500">Loading micronutrient data...</p>
        </div>
      </div>
    );
  }

  const dailyAverages = micronutrientData.daily_averages;

  // Helper function to get target value (personalized or RDA fallback)
  const getTargetValue = (nutrientKey: string) => {
    // If user has personalized recommendation and it's not empty/null, use it
    if (userRecommendations && userRecommendations[nutrientKey] && Number(userRecommendations[nutrientKey]) > 0) {
      return {
        value: Number(userRecommendations[nutrientKey]),
        unit: (RDA_VALUES as any)[nutrientKey]?.unit || 'g',
        isPersonalized: true
      };
    }

    // Fall back to standard RDA
    const rdaValue = (RDA_VALUES as any)[nutrientKey];
    return {
      value: rdaValue?.value || 0,
      unit: rdaValue?.unit || 'g',
      isPersonalized: false
    };
  };

  // Group nutrients by category
  const nutrientCategories = {
    'Macronutrients': ['calories', 'protein', 'carbs', 'fat', 'fiber'],
    'Vitamins': [
      'vitamin_a', 'vitamin_c', 'vitamin_d', 'vitamin_e', 'vitamin_k',
      'thiamine_b1', 'riboflavin_b2', 'niacin_b3', 'pantothenic_acid_b5',
      'pyridoxine_b6', 'biotin_b7', 'folate_b9', 'vitamin_b12'
    ],
    'Minerals': [
      'calcium', 'iron', 'magnesium', 'zinc', 'potassium', 'sodium',
      'phosphorus', 'copper', 'selenium', 'chromium', 'molybdenum', 'iodine'
    ],
    'Amino Acids': [
      'histidine', 'isoleucine', 'leucine', 'lysine', 'methionine',
      'phenylalanine', 'threonine', 'tryptophan', 'valine'
    ],
    'Fatty Acids': [
      'saturated', 'monounsaturated', 'polyunsaturated', 'omega_3', 'omega_6'
    ]
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium text-gray-900">
          Comprehensive Micronutrient Analysis (44 Nutrients)
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          {micronutrientData.actual_days ? `${micronutrientData.actual_days}-day average` : '14-day average'} vs Recommended Daily Allowance • {micronutrientData.meal_count} meals analyzed
          {micronutrientData.actual_days && ` • ${micronutrientData.actual_days} days with logged meals`}
        </p>
      </div>
      
      <div className="card-body space-y-8">
        {Object.entries(nutrientCategories).map(([categoryName, nutrients]) => (
          <div key={categoryName}>
            <h4 className="text-md font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
              {categoryName}
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {nutrients.map((nutrientKey) => {
                const value = dailyAverages[nutrientKey] || 0;
                const target = getTargetValue(nutrientKey);

                if (!target || target.value === 0) return null;

                const percentage = (value / target.value) * 100;

                return (
                  <div
                    key={nutrientKey}
                    className={`p-3 rounded-lg border-2 ${getColorClass(value, target.value)}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-lg">
                        {getStatusIcon(value, target.value)}
                      </span>
                      <div className="text-xs text-gray-600">
                        {percentage.toFixed(0)}%
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium text-sm leading-tight">
                        {formatNutrientName(nutrientKey)}
                      </h5>
                      <div className="text-xs text-gray-600 mt-1">
                        Target: {formatValue(target.value)} {target.unit}
                        {target.isPersonalized && (
                          <span className="ml-1 text-blue-600" title="Personalized target">✨</span>
                        )}
                      </div>
                    </div>

                    <div className="mt-2">
                      <div className="text-lg font-bold">
                        {formatValue(value)}
                      </div>
                      <div className="text-xs text-gray-600">
                        {target.unit}/day
                      </div>

                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${
                              percentage >= 100 ? 'bg-green-500' :
                              percentage >= 75 ? 'bg-yellow-500' :
                              percentage >= 50 ? 'bg-orange-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        
        {/* Summary & Legend */}
        <div className="pt-6 border-t border-gray-200">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Quick Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Quick Analysis</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Meeting targets (≥100%):</span>
                  <span className="font-medium text-green-600">
                    {Object.keys(dailyAverages).filter(key => 
                      (RDA_VALUES as any)[key] && (dailyAverages[key] / (RDA_VALUES as any)[key].value) >= 1
                    ).length} nutrients
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Significantly low (&lt;50%):</span>
                  <span className="font-medium text-red-600">
                    {Object.keys(dailyAverages).filter(key => 
                      (RDA_VALUES as any)[key] && (dailyAverages[key] / (RDA_VALUES as any)[key].value) < 0.5
                    ).length} nutrients
                  </span>
                </div>
              </div>
            </div>
            
            {/* Legend */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Status Legend</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  <span className="mr-2">✅</span>
                  <span>Meeting RDA (100%+)</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-2">⚠️</span>
                  <span>Close to target (75-99%)</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-2">🟡</span>
                  <span>Below target (50-74%)</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-2">🔴</span>
                  <span>Significantly low (&lt;50%)</span>
                </div>
                <div className="flex items-center mt-3 pt-3 border-t border-gray-300">
                  <span className="mr-2 text-blue-600">✨</span>
                  <span>Personalized target (based on your profile)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};